import { useCallback, useEffect, useRef, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import clsx from 'clsx';

const HOLD_MS = 1500;

interface Props {
  onSigned: () => void;
  disabled?: boolean;
  label?: string;
  holdingLabel?: string;
  testid?: string;
}

/**
 * Deliberate "hold to sign" commit gesture. The customer must press and hold for
 * ~1.5s (a progress bar fills); releasing early cancels. Keyboard-accessible via
 * holding Space/Enter. This is the deliberate confirmation layered on top of the
 * typed-name signature — not the signature itself.
 */
export default function HoldToSign({
  onSigned,
  disabled = false,
  label = 'Hold to sign',
  holdingLabel = 'Keep holding…',
  testid = 'hold-to-sign',
}: Props) {
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef(0);
  const firedRef = useRef(false);

  const reduceMotion =
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  const stop = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setHolding(false);
    setProgress(0);
  }, []);

  const tick = useCallback(() => {
    const elapsed = performance.now() - startRef.current;
    const p = Math.min(1, elapsed / HOLD_MS);
    setProgress(p);
    if (p >= 1) {
      if (!firedRef.current) {
        firedRef.current = true;
        onSigned();
      }
      stop();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [onSigned, stop]);

  const start = useCallback(() => {
    if (disabled || holding) return;
    firedRef.current = false;
    startRef.current = performance.now();
    setHolding(true);
    rafRef.current = requestAnimationFrame(tick);
  }, [disabled, holding, tick]);

  const cancel = useCallback(() => {
    if (!firedRef.current) stop();
  }, [stop]);

  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);

  // Quantise the fill in reduced-motion mode so it steps rather than glides.
  const shown = reduceMotion ? Math.round(progress * 4) / 4 : progress;

  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={start}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onKeyDown={(e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
          e.preventDefault();
          start();
        }
      }}
      onKeyUp={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          cancel();
        }
      }}
      data-testid={testid}
      aria-label={label}
      className={clsx(
        'relative w-full select-none overflow-hidden rounded-full px-5 py-3',
        'font-inter text-sm font-semibold uppercase tracking-wider',
        'bg-gold-bright text-ink-black transition',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-bright focus-visible:ring-offset-2 focus-visible:ring-offset-ink-black',
      )}
    >
      <span className="relative flex items-center justify-center gap-2">
        <ShieldCheck size={14} />
        {holding ? holdingLabel : label}
      </span>
      <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 bg-ink-black/15">
        <span className="block h-full bg-ink-black/70" style={{ width: `${shown * 100}%` }} />
      </span>
    </button>
  );
}
